import React, { useState, useCallback } from 'react';
import { FaEnvelope, FaLock, FaUserPlus, FaKey, FaShieldAlt } from 'react-icons/fa';
import SimpleCaptcha from './SimpleCaptcha';
import InternalLinks from './InternalLinks';
import GoogleSignInButton, { getGoogleClientId } from './GoogleSignInButton';

const Login = ({ onLogin, onGoogleAuth, onSwitchToRegister, onForgotPassword, onNavigate }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutMinutes, setLockoutMinutes] = useState(0);
  const [honeypot, setHoneypot] = useState(''); // Bot trap field
  const [googleLoading, setGoogleLoading] = useState(false);

  const googleEnabled = !!getGoogleClientId();

  const handleGoogleCredential = useCallback(async (credential) => {
    setErrorMessage('');
    setGoogleLoading(true);
    try {
      await onGoogleAuth(credential);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setErrorMessage(detail || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  }, [onGoogleAuth]);

  const handleCaptchaVerify = useCallback((token, answer) => {
    setCaptchaToken(token);
    setCaptchaAnswer(answer);
    // Clear captcha error when user provides an answer
    if (token && answer) {
      setErrors(prev => prev.captcha ? { ...prev, captcha: '' } : prev);
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    // Honeypot check - if filled, silently reject (bot detected)
    if (honeypot) {
      console.log('Bot detected via honeypot');
      return false;
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    }

    // Only validate CAPTCHA if it's being shown
    if (showCaptcha && (!captchaToken || !captchaAnswer)) {
      newErrors.captcha = 'Please solve the math problem';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Pass CAPTCHA only if it's being shown
      await onLogin(
        email,
        password,
        showCaptcha ? captchaToken : null,
        showCaptcha ? captchaAnswer : null
      );
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Login failed. Please try again.';
      setErrorMessage(detail);

      // Check if CAPTCHA is now required (from response header or error message)
      const captchaHeader = error.response?.headers?.['x-captcha-required'];
      if (captchaHeader === 'true' || detail.includes('CAPTCHA')) {
        setShowCaptcha(true);
      }

      // Check for lockout (429 status)
      if (error.response?.status === 429) {
        setIsLockedOut(true);
        // Extract minutes from error message
        const match = detail.match(/(\d+)\s*minutes?/);
        if (match) {
          setLockoutMinutes(parseInt(match[1], 10));
        }
      }

      // Refresh captcha on error if it's showing
      if (showCaptcha) {
        setCaptchaKey(prev => prev + 1);
        setCaptchaToken('');
        setCaptchaAnswer('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md animate-scale-in">
      <div className="bg-white rounded-2xl shadow-elevated p-8">
        {/* Logo & Welcome */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <img src="/logo.png" alt="ItinerarySeek" className="h-16 w-auto" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-500 to-secondary-400 bg-clip-text text-transparent mb-2">
            Welcome Back
          </h1>
          <p className="text-neutral-600">Sign in to continue your journey</p>
        </div>

        {/* Lockout Banner */}
        {isLockedOut && (
          <div className="bg-amber-50 border-l-4 border-amber-500 text-amber-700 p-4 rounded-lg mb-6 animate-slide-down">
            <div className="flex items-center gap-2">
              <FaShieldAlt className="flex-shrink-0" />
              <p className="text-sm">
                Too many failed attempts. Please try again in {lockoutMinutes} minutes.
              </p>
            </div>
          </div>
        )}

        {/* Error Banner */}
        {errorMessage && !isLockedOut && (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded-lg mb-6 animate-slide-down">
            <p className="text-sm">{errorMessage}</p>
          </div>
        )}

        {/* Google Sign-In above the divider. Unlike the register page the email
            form stays expanded - returning password users should not have to
            click twice to reach the field they came for. */}
        {googleEnabled && (
          <div className="mb-6">
            <GoogleSignInButton
              mode="signin"
              onCredential={handleGoogleCredential}
              disabled={googleLoading || loading || isLockedOut}
            />

            {googleLoading && (
              <div className="flex items-center justify-center gap-2 mt-3 text-sm text-neutral-600">
                <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                Signing you in...
              </div>
            )}

            <div className="flex items-center gap-3 mt-6">
              <div className="flex-1 h-px bg-neutral-200"></div>
              <span className="text-xs font-medium text-neutral-400 uppercase tracking-wide">
                or sign in with email
              </span>
              <div className="flex-1 h-px bg-neutral-200"></div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - hidden from users, visible to bots */}
          <input
            type="text"
            name="website"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            autoComplete="off"
            tabIndex="-1"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          />

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
              <FaEnvelope className="text-primary-500" />
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                errors.email
                  ? 'border-red-500 bg-red-50'
                  : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
              } outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={loading || isLockedOut}
            />
            {errors.email && (
              <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                {errors.email}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
              <FaLock className="text-primary-500" />
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                errors.password
                  ? 'border-red-500 bg-red-50'
                  : 'border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
              } outline-none disabled:opacity-50 disabled:cursor-not-allowed`}
              disabled={loading || isLockedOut}
            />
            {errors.password && (
              <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                {errors.password}
              </p>
            )}
          </div>

          {/* Forgot Password Link */}
          <div className="text-right">
            <button
              type="button"
              onClick={onForgotPassword}
              disabled={loading}
              className="text-primary-600 text-sm font-medium hover:text-primary-700 hover:underline transition-colors inline-flex items-center gap-1 disabled:opacity-50"
            >
              <FaKey className="text-xs" />
              Forgot Password?
            </button>
          </div>

          {/* CAPTCHA - Only shown after failed attempts */}
          {showCaptcha && (
            <div className="animate-slide-down">
              <div className="flex items-center gap-2 text-sm text-amber-600 mb-2">
                <FaShieldAlt />
                <span>Security verification required</span>
              </div>
              <SimpleCaptcha
                key={captchaKey}
                onVerify={handleCaptchaVerify}
                disabled={loading || isLockedOut}
              />
              {errors.captcha && (
                <p className="text-red-600 text-sm mt-2">{errors.captcha}</p>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || isLockedOut}
            className="w-full bg-gradient-primary text-white py-3 px-6 rounded-lg font-semibold text-lg shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Signing In...
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        {/* Switch to Register */}
        <div className="mt-6 text-center">
          <p className="text-neutral-600">
            Don't have an account?{' '}
            <button
              onClick={onSwitchToRegister}
              disabled={loading}
              className="text-primary-600 font-semibold hover:text-primary-700 hover:underline transition-colors inline-flex items-center gap-1 disabled:opacity-50"
            >
              <FaUserPlus />
              Create Account
            </button>
          </p>
        </div>
      </div>

      {/* Internal Links for SEO */}
      <div className="mt-6 pt-4 border-t border-white/20">
        <p className="text-sm text-white/70 text-center">
          Explore:{' '}
          <a href="/about" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('about'); }} className="text-white/90 hover:text-white hover:underline">About</a>
          {' · '}
          <a href="/pricing" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('pricing'); }} className="text-white/90 hover:text-white hover:underline">Pricing</a>
          {' · '}
          <a href="/faq" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('faq'); }} className="text-white/90 hover:text-white hover:underline">FAQ</a>
          {' · '}
          <a href="/blog" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('blog'); }} className="text-white/90 hover:text-white hover:underline">Blog</a>
          {' · '}
          <a href="/contact" onClick={(e) => { e.preventDefault(); onNavigate && onNavigate('contact'); }} className="text-white/90 hover:text-white hover:underline">Contact</a>
        </p>
      </div>

      {/* Decorative Quote */}
      <p className="text-center text-white/80 mt-4 italic text-sm">
        "The world is a book, and those who do not travel read only one page."
      </p>
    </div>
  );
};

export default Login;
